import { useState, useEffect, useCallback } from 'react';
import { httpGet } from '@sdk/api/api';
import { GifData } from '@sdk/components/richText/types';
import useDebounce from '@sdk/utils/hooks/useDebounce';
import useConfig from '@sdk/config/useConfig';

import setInClientCache from '@web/common/utils/setInClientCache';
import getFromClientCache from '@web/common/utils/getFromClientCache';

import { RECENT_GIFS_CACHE_KEY, SEARCH_URL } from './gifPicker.constants';
import { GiphyGif } from './gifPicker.types';

export const useRecentGifs = () => {
  const [recentGifs, setRecentGifs] = useState<GifData[]>([]);

  useEffect(() => {
    const cachedRecentGifs = getFromClientCache(RECENT_GIFS_CACHE_KEY, []);
    setRecentGifs(cachedRecentGifs);
  }, []);

  const addGifToRecent = (gif: GifData) => {
    const updatedRecentGifs = recentGifs
      .filter((g) => g.originalUrl !== gif.originalUrl)
      .slice(0, 9);
    updatedRecentGifs.unshift(gif);
    setInClientCache(RECENT_GIFS_CACHE_KEY, updatedRecentGifs);

    setRecentGifs(updatedRecentGifs);
  };

  return {
    recentGifs,
    addGifToRecent,
  };
};

export const useGifSearch = () => {
  const config = useConfig();
  const GIPHY_API_KEY = config?.GIPHY_API_KEY;
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 200);
  const [searchResults, setSearchResults] = useState<GifData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const search = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const res = await httpGet(
        SEARCH_URL,
        {
          api_key: GIPHY_API_KEY,
          q: debouncedSearch,
          limit: 10,
        },
        {
          noPrefix: true,
        }
      );

      const gifs = res.data.data.map((g: GiphyGif) => ({
        previewUrl: g.images.fixed_height.url,
        originalUrl: g.images.original.url,
      }));
      setSearchResults(gifs);
    } catch (e) {
      // err
    } finally {
      setIsLoading(false);
    }
  }, [GIPHY_API_KEY, debouncedSearch]);

  useEffect(() => {
    if (debouncedSearch) {
      search();
    }
  }, [search, debouncedSearch]);

  return {
    isLoading,
    searchResults,
    debouncedSearch,
    searchQuery,
    setSearchQuery,
  };
};
